import React, {useState, useMemo, useContext, useCallback} from 'react'
import {TIMELINE_CHOICES, RECIPIENT_CHOICES, INTENT_CHOICES} from './data'
import {CalendarSelect, NotSure} from './shared'
import {Frame, FrameHeader, CheckMarkCard, HelpModal, HelpLink} from 'js/studios/shared/flows'
import {FlowFramesCtx} from 'js/studios/shared/unredux/contexts/flow'
import {KeyValueCtx} from 'js/studios/shared/unredux/contexts/key_value'

export function RingRecipientFrame() {
  const {gotoNextFrame} = useContext(FlowFramesCtx)
  const {
    data: {recipient, intent, significantOtherName, significantOtherNameSkip},
    boundSetter, setMany,
  } = useContext(KeyValueCtx)

  const
    MAX_NAME_LENGTH = 250,
    [helpOpen, setHelpOpen] = useState(false),
    nameError = useMemo(() => {
      if (significantOtherName) {
        if (significantOtherName.length > MAX_NAME_LENGTH) {
          return "Name must be shorter, sorry."
        } else if (/https?:/.test(significantOtherName) || /www\./.test(significantOtherName) || /\.(com|net|org)/.test(significantOtherName)) {
          return "That looks like a URL, please enter your significant other's name."
        }
      }
      return null
    }, [significantOtherName]),
    onChangeRecipient = useCallback(ev => {
      ev.preventDefault()
      setMany({
        recipient: ev.target.value,
        intent: "",
        significantOtherName: "",
        significantOtherNameSkip: false,
      })
    }, [setMany]),
    openHelp = useCallback(ev => {ev.preventDefault(); setHelpOpen(true)}, []),
    closeHelp = useCallback(ev => {ev.preventDefault(); setHelpOpen(false)}, []),
    canSubmit = useMemo(() => {
      if (recipient === "S/O") {
        return !!(significantOtherNameSkip || (significantOtherName && !nameError))
      } else if (recipient === "me") {
        return !!intent
      }
      return false
    }, [recipient, significantOtherName, significantOtherNameSkip, nameError, intent])

  return <Frame canSubmit={canSubmit} onSubmit={gotoNextFrame}>
    <FrameHeader h2ClassName="mr--1">Who will be wearing the ring?</FrameHeader>
    <p className="txt--center mb--lg">Are you designing a ring for your significant other, or designing your own ring?</p>
    <select aria-label="Ring recipient" className="field--lg mb--lg" onChange={onChangeRecipient} value={recipient}>
      <option value="" disabled>Choose recipient</option>
      {RECIPIENT_CHOICES.map(choice => <option key={choice.value} value={choice.value}>{choice.text}</option>)}
    </select>
    {recipient === 'S/O' && <div className="txt--center field--lg flex-col anim--fade-in">
      <p>Who’s the lucky guy or gal?</p>
      <label className="field--lg mb--1">
        <input
          type="text"
          aria-label="Significant other's first name"
          disabled={significantOtherNameSkip}
          placeholder="Your S/O’s first name"
          value={significantOtherName}
          onChange={boundSetter("significantOtherName")}
          />
        <HelpLink onClick={openHelp} />
      </label>
      {nameError && <div className="flow-error">{nameError}</div>}
      <NotSure field="significantOtherNameSkip" fieldsToWipe={["significantOtherName"]} checked={significantOtherNameSkip}>I'd prefer not to say.</NotSure>
    </div>}
    {recipient === 'me' && <div className="txt--center field--lg flex-col anim--fade-in">
      <p>Looking around for a ring someone might surprise you with later, or are you planning to purchase your own ring?</p>
      <select aria-label="Looking around or shopping for your own ring?" className="field--lg" onChange={boundSetter("intent")} value={intent}>
        <option value="" disabled>Select response</option>
        {INTENT_CHOICES.map(choice => <option key={choice.value} value={choice.value}>{choice.text}</option>)}
      </select>
    </div>}
    <HelpModal name="significant-other-name" isOpen={helpOpen} onClose={closeHelp}>
      <p className="mb--0">
        Knowing your S/O's name just helps us keep our communication clear. Don't worry though, we won't tell them there's a ring in the works!
      </p>
    </HelpModal>
  </Frame>
}

const CENTER_STONE_CARDS = [
  {
    name: "diamond",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/engagement/center-stone/diamond.jpg`,
    label: "Diamond"
  },
  {
    name: "gem",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/engagement/center-stone/colored-gemstone.jpg`,
    label: "Colored Gemstone"
  },
  {
    name: "moissanite",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/engagement/center-stone/moissanite.jpg`,
    label: "Moissanite"
  },
  {
    name: "no_stone",
    img: `${window.cm.structureUrl}/static/images/brand2025/project-creation/engagement/center-stone/no-stone.jpg`,
    label: "No Stone"
  }
]

export function CenterStoneFrame() {
  const {gotoNextFrame} = useContext(FlowFramesCtx)
  const {
    data: {centerStoneTypes, centerStoneSkip},
    addToKey, rmFromKey, setKey,
  } = useContext(KeyValueCtx)

  const
    isChecked = name => centerStoneTypes.includes(name),
    onClickStone = useCallback(ev => {
      // cpg is special: checking it sets the entire list to just [cpg], unchecking clears the list
      if (ev.target.value === "cpg") {
        if (centerStoneTypes.includes(ev.target.value)) {
          setKey("centerStoneTypes", [])
        } else {
          setKey("centerStoneTypes", ["cpg"])
        }
        return
      }

      // all other types
      if (centerStoneTypes.includes(ev.target.value)) {
        rmFromKey("centerStoneTypes", ev.target.value)
      } else {
        addToKey("centerStoneTypes", ev.target.value)
      }
    }, [centerStoneTypes, addToKey, rmFromKey, setKey]),
    canSubmit = centerStoneTypes?.length || centerStoneSkip

  return <Frame canSubmit={canSubmit} onSubmit={gotoNextFrame}>
    <FrameHeader>Your center stone.</FrameHeader>
    <p className="txt--center mb--lg">What types of center stones might you be considering for your ring?</p>
    <p className="txt--center txt--caption mb--1">Select all that apply</p>
    <div className="check-mark-cards-list check-mark-cards-list--2col">
      {CENTER_STONE_CARDS.map(card => <CheckMarkCard
        key={card.name}
        imgs={[card.img]}
        value={card.name}
        description={card.label}
        checked={isChecked(card.name)}
        disabled={centerStoneSkip}
        onClick={onClickStone}
      />)}
      <CheckMarkCard
        className="check-mark-card--spread check-mark-card--thin txt--center"
        description="I have my own diamond or gemstone I’d like to use"
        value="cpg"
        checked={isChecked("cpg")}
        disabled={centerStoneSkip}
        onClick={onClickStone}
        />
      <NotSure field="centerStoneSkip" fieldsToWipe={['centerStoneTypes']} checked={centerStoneSkip}>I’m not sure.</NotSure>
    </div>
  </Frame>
}

export function DesignVisionFrame() {
  const DESIGN_VISION_TYPES = [
    {name: "has_images_to_share", description: "I have images of inspiration ring(s) that I can share."},
    {name: "has_details_to_share", description: "I have details or ideas I can share."},
  ]

  const {gotoNextFrame} = useContext(FlowFramesCtx)
  const {
    data: {designVisionSources, designVisionSkip},
    addToKey, rmFromKey,
  } = useContext(KeyValueCtx)

  const
    isChecked = name => designVisionSources.includes(name),
    onClickDesignVision = useCallback(ev => {
      if (designVisionSources.includes(ev.target.value)) {
        rmFromKey("designVisionSources", ev.target.value)
      } else {
        addToKey("designVisionSources", ev.target.value)
      }
    }, [designVisionSources, addToKey, rmFromKey]),
    canSubmit = designVisionSources?.length || designVisionSkip

  return <Frame canSubmit={canSubmit} onSubmit={gotoNextFrame}>
    <FrameHeader>It starts with style.</FrameHeader>
    <p className="txt--center mb--lg">Already know what ring style you’re going for, or are you not sure quite yet?</p>
    <div className="txt--center txt--caption mb--1">Select all that apply</div>
    <div className="check-mark-cards-list">
      {DESIGN_VISION_TYPES.map(card => <CheckMarkCard
        key={card.name}
        className="check-mark-card--thin"
        value={card.name}
        description={card.description}
        checked={isChecked(card.name)}
        disabled={designVisionSkip}
        onClick={onClickDesignVision}
      />)}
      <NotSure field="designVisionSkip" fieldsToWipe={['designVisionSources']} checked={designVisionSkip}>I have no ideas yet - I just want an amazing ring!</NotSure>
    </div>
  </Frame>
}

export function DeliveryDateFrame() {
  const
    {gotoNextFrame} = useContext(FlowFramesCtx),
    {data: {timeline, deliveryDate}, setKey, boundSetter} = useContext(KeyValueCtx),
    onSelectTimeline = ev => {
      setKey("timeline", ev.target.value)
      setKey("deliveryDate", null)
    }

  const canSubmit = (timeline && timeline !== 'specific_date') || (timeline === 'specific_date' && deliveryDate)

  return <Frame canSubmit={canSubmit} onSubmit={gotoNextFrame}>
    <FrameHeader>When’s the special day?</FrameHeader>
    <div className="txt--center mb--lg">
      <p>
        If you have a planned proposal in mind, let us know so we can make sure your project stays on track.
        If you don’t have a date yet, or you’re just starting to look around, that’s okay too!
      </p>
      <p className="txt--body-2 txt--wrap-balance mb--0">Note that the minimum time required for production &mdash; once we’ve agreed on a design &mdash; is 3 weeks.</p>
    </div>
    <select aria-label="Project timeline" className="field--lg" onChange={onSelectTimeline} value={timeline}>
      <option value="" disabled>Choose a response</option>
      {TIMELINE_CHOICES.map(choice => <option key={choice.value} value={choice.value}>{choice.text}</option>)}
    </select>
    {timeline === 'specific_date' && <div className="field--lg mt--1">
      <CalendarSelect value={deliveryDate} onChange={boundSetter("deliveryDate")} />
    </div>}
  </Frame>
}
